/**
 * A layer that owns focus while it is open (a dialog, the palette): the
 * dialog pattern in one place (2026-09-08). On open, focus moves to the first
 * focusable inside `ref`, or to the container itself (which carries tabIndex
 * -1 for that), unless a child already took it (an autoFocus field). While
 * open, Tab and Shift+Tab cycle inside the layer. On close, focus goes back
 * to whatever had it before the layer opened.
 */

import { useEffect, useState, type RefObject } from 'react';

const FOCUSABLE =
  'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

/** The tabbable elements inside `root`, in document order. */
function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) =>
      !el.hasAttribute('disabled') &&
      el.getAttribute('aria-hidden') !== 'true' &&
      el.getAttribute('tabindex') !== '-1' &&
      el.closest('[inert]') === null,
  );
}

function activeElement(): HTMLElement | null {
  return document.activeElement instanceof HTMLElement ? document.activeElement : null;
}

export function useFocusLayer(ref: RefObject<HTMLElement | null>, open: boolean): void {
  // Where focus was as the layer opened, read while rendering: by the time
  // an effect runs, an autoFocus child has already taken it.
  const [wasOpen, setWasOpen] = useState(open);
  const [before, setBefore] = useState<HTMLElement | null>(() => (open ? activeElement() : null));
  if (open !== wasOpen) {
    setWasOpen(open);
    setBefore(open ? activeElement() : null);
  }

  useEffect(() => {
    if (!open) return;
    const root = ref.current;
    if (root === null) return;
    if (!root.contains(document.activeElement)) (focusables(root)[0] ?? root).focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusables(root);
      const first = items[0] ?? root;
      const last = items[items.length - 1] ?? root;
      const active = document.activeElement;
      const atEdge = e.shiftKey ? active === first : active === last;
      if (atEdge || !root.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (before !== null && before.isConnected) before.focus();
    };
  }, [ref, open, before]);
}
